import axios from 'axios.js';

const stripTrailingSlash = (s) => s.replace(/\/+$/, '');
const stripLeadingSlash = (s) => s.replace(/^\/+/, '');
// Photos paths coming from the DB have shown up with backslashes in this
// project - normalize them to forward slashes so they're safe to put in a URL.
const normalizeSlashes = (s) => s.replace(/\\/g, '/');

// Given an API base URL like
//   http://localhost:8084/Product/KiTReact/api/0977b144_1507/
// returns the app root that actually serves /assets/... :
//   http://localhost:8084/Product/KiTReact
// by cutting everything from the "/api" path segment onward - the api
// endpoint and tenant-hash segments don't apply to static asset paths.
const deriveAssetBaseFromApiUrl = (apiUrl) => {
  if (!apiUrl || typeof apiUrl !== 'string') return null;
  try {
    const url = new URL(apiUrl, window.location.origin);
    const match = url.pathname.match(/^(.*?)\/api(\/|$)/i);
    const rootPath = match ? match[1] : stripTrailingSlash(url.pathname);
    return `${url.origin}${rootPath}`;
  } catch (e) {
    return null;
  }
};

const getStoredApiBaseUrl = () => {
  try {
    return window.localStorage.getItem('baseURL');
  } catch (e) {
    return null;
  }
};

let hasLoggedAssetBase = false;
const logAssetBaseOnce = (source, value) => {
  if (process.env.NODE_ENV === 'production' || hasLoggedAssetBase || typeof window === 'undefined') return;
  hasLoggedAssetBase = true;
  // eslint-disable-next-line no-console
  console.info(`[resolvePhotoUrl] using asset base from ${source}: "${value}"`);
};

// ============================================================================
// Ordered list of URLs worth trying for a stored photo path, most likely
// correct first. <PhotoAvatar> (see PhotoAvatar.jsx) tries each in order and
// only falls back to initials once every candidate has failed to load.
//
//  1. localStorage 'baseURL' (the same value axios.js uses for API calls,
//     set dynamically per tenant/session), with the /api/{tenant}/ suffix
//     stripped off. Derived automatically - no per-environment config needed.
//  2. REACT_APP_ASSET_BASE_URL env var, if you want to force a specific
//     value (e.g. on a screen where localStorage isn't populated yet).
//  3. axios.defaults.baseURL, same derivation, as a fallback.
//  4. Same-origin relative path, as a last resort.
// ============================================================================
export const getPhotoUrlCandidates = (photoPath) => {
  if (!photoPath || typeof photoPath !== 'string') return [];
  if (/^(https?:|blob:|data:)/i.test(photoPath)) return [photoPath];

  const path = stripLeadingSlash(normalizeSlashes(photoPath));
  const candidates = [];

  const derivedFromStorage = deriveAssetBaseFromApiUrl(getStoredApiBaseUrl());
  if (derivedFromStorage) {
    logAssetBaseOnce("localStorage 'baseURL'", derivedFromStorage);
    candidates.push(`${derivedFromStorage}/${path}`);
  }

  const envBase = process.env.REACT_APP_ASSET_BASE_URL ? stripTrailingSlash(process.env.REACT_APP_ASSET_BASE_URL) : null;
  if (envBase) {
    logAssetBaseOnce('REACT_APP_ASSET_BASE_URL', envBase);
    candidates.push(`${envBase}/${path}`);
  }

  const derivedFromAxios = deriveAssetBaseFromApiUrl(axios.defaults.baseURL);
  if (derivedFromAxios) candidates.push(`${derivedFromAxios}/${path}`);

  candidates.push(`/${path}`);

  if (!derivedFromStorage && !envBase && process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && !hasLoggedAssetBase) {
    hasLoggedAssetBase = true;
    // eslint-disable-next-line no-console
    console.warn("[resolvePhotoUrl] couldn't find localStorage 'baseURL' or REACT_APP_ASSET_BASE_URL - falling back to guesses.");
  }

  // De-dupe in case two strategies land on the same URL.
  return [...new Set(candidates)];
};

// Single-URL convenience wrapper (first/best candidate only) for call sites
// that don't need the cascading-fallback behavior.
export const resolvePhotoUrl = (photoPath) => getPhotoUrlCandidates(photoPath)[0];

export default resolvePhotoUrl;
